import produce from 'immer';

export type Block = {
  type: string;
  id: string;
  position: number;
  contentId: string;
  text?: string;
  setType: (type: string) => void;
  url?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  addImage: (url: string, imageId: string) => void;
  addSound: (url: string, soundId: string) => void;
  addVideo: (url: string, videoId: string) => void;
  setDocument: (documentId: Document | null) => void;
  updateUrl: (url: string) => void;
  updateText: (html: string) => void;
  delete: () => void;
};

interface Document {
  id: string;
  url: string;
}

export type InstructionalContent = {
  id: string;
  templateType: 'instructional_contents';
  icSubtype: 'instructional_page' | 'instructional_document';
  title: string;
  updateTitle: (title: string) => void;
  add: () => void;
  order: (blockId: string, position: string) => void;
  blocks: Block[];
};

export default function instructional_contents({ item, setItem, itemsService, annotations }): InstructionalContent {
  const updateTitle = (text) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.title = text;
      })
    );

    itemsService.debouncedUpdate({
      pathParams: { id: item.id },
      data: { data: { attributes: { title: String(text) } } },
      debounceKey: `instructional_content_title`,
    });
  };

  const add = async () => {
    const tempId = `placeholder-id-${Date.now()}`;

    setItem((prev) =>
      produce(prev, (draft) => {
        draft.blocks = [...draft.blocks, { id: tempId, position: draft.blocks.length + 1 }];
      })
    );
    const newAnnotation = await annotations.create({ pathParams: { id: item.id } });
    setItem((prev) =>
      produce(prev, (draft) => {
        const blocks = draft.blocks.filter((a) => a.id !== tempId);
        draft.blocks = [...blocks, createBlockFromAnnotation(newAnnotation)];
      })
    );
    setOpen(newAnnotation.id);
  };

  const order = (blockId: string, position: string) => {
    annotations.order({
      pathParams: { id: blockId },
      data: { data: { attributes: { position: Number(position) } } },
    });
  };

  const setType = (blockId: string, type: string) => {
    // we need to manually set some values when we set the block type
    const extraContent =
      {
        embed: {
          'external-resource-url': '<iframe src="#" height="100%" width="100%" />',
        },
        text: { 'text-html': '<p></p>' },
      }[type] || {};

    annotations.update({
      pathParams: { id: blockId },
      data: { data: { attributes: { 'note-type': type, ...extraContent } } },
    });
    setItem((prev) =>
      produce(prev, (draft) => {
        const block = draft.blocks.find((a) => a.id === blockId);
        if (block) {
          block.type = type;
        }
      })
    );
  };

  const deleteNote = (id: string) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        draft.blocks = draft.blocks.filter((a) => a.id !== id);
      })
    );
    annotations.delete({ pathParams: { id } });
  };

  const setOpen = (id: string) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        draft.blocks = draft.blocks.map((block) => ({ ...block, open: block.id === id && !block.open }));
      })
    );
  };

  const updateText = async (conceptId, html) => {
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const blockIndex = draft.blocks.findIndex((a) => a.id === conceptId);
        if (blockIndex > -1) {
          draft.blocks[blockIndex].text = html;
        }
      })
    );
    await annotations.debouncedUpdate({
      pathParams: { id: conceptId },
      data: { id: conceptId, data: { attributes: { 'text-html': String(html) } } },
      debounceKey: `instructional_content_text_${conceptId}`,
    });
  };

  // Helper function to update URL in the local state
  const updateBlockUrl = (conceptId, url) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        const blockIndex = draft.blocks.findIndex((a) => a.id === conceptId);
        if (blockIndex > -1) {
          draft.blocks[blockIndex].url = url;
        }
      })
    );
  };

  const updateUrl = async (conceptId, url) => {
    // Update local state
    updateBlockUrl(conceptId, url);

    // Send API request
    await annotations.update({
      pathParams: { id: conceptId },
      data: { id: conceptId, data: { attributes: { 'external-resource-url': String(url) } } },
    });
  };

  const addImage = async (conceptId, url, imageId) => {
    // Update local state
    updateBlockUrl(conceptId, url);

    // Send API request
    await annotations.update({
      pathParams: { id: conceptId },
      data: { data: { relationships: { image: { data: { id: imageId } } } } },
    });
  };

  const addSound = async (conceptId, url, soundId) => {
    // Update local state
    updateBlockUrl(conceptId, url);

    // Send API request
    await annotations.update({
      pathParams: { id: conceptId },
      data: { data: { relationships: { sound: { data: { id: soundId } } } } },
    });
  };

  const addVideo = async (conceptId, url, videoId) => {
    // Update local state
    updateBlockUrl(conceptId, url);

    // Send API request
    await annotations.update({
      pathParams: { id: conceptId },
      data: { data: { relationships: { video: { data: { id: videoId } } } } },
    });
  };

  const setDocument = async (conceptId, document: Document | null) => {
    // Update local state with document URL
    updateBlockUrl(conceptId, document?.url || '');

    // Prepare and send API request
    const documentData = document ? { id: document?.id } : null;
    await annotations.update({
      pathParams: { id: conceptId },
      data: { data: { relationships: { document: { data: documentData } } } },
    });
  };

  function createBlockFromAnnotation(annotation) {
    return {
      type: annotation.noteType,
      setType: (type) => setType(annotation.id, type),
      delete: () => deleteNote(annotation.id),
      id: annotation.id,
      open: false,
      setOpen: () => setOpen(annotation.id),
      position: annotation.position,
      contentId: annotation.image?.id || annotation.sound?.id || annotation.video?.id,
      text: annotation.textHtml,
      addImage: (url, imageId) => addImage(annotation.id, url, imageId),
      addSound: (url, soundId) => addSound(annotation.id, url, soundId),
      addVideo: (url, videoId) => addVideo(annotation.id, url, videoId),
      updateText: (html) => updateText(annotation.id, html),
      updateUrl: (url) => updateUrl(annotation.id, url),
      setDocument: (documentId) => setDocument(annotation.id, documentId),
      url:
        annotation.externalResourceUrl ||
        annotation.document?.url ||
        annotation.image?.url ||
        annotation.sound?.url ||
        annotation.video?.url,
    };
  }

  return {
    id: item.id,
    templateType: 'instructional_contents',
    icSubtype: item.icSubtype,
    title: item.title,
    updateTitle,
    add,
    order,
    blocks: item.annotations.map(createBlockFromAnnotation),
  };
}
